import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import NewCheckbox from 'client/components/core/Sidebar/components/Settings/components/NewCheckbox'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { FontSize } from 'client/components/core/Sidebar/components/Settings/options'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import Border from 'client/components/core/Sidebar/components/Settings/styles/Border'
import FlexAlignStyle from 'client/components/core/Sidebar/components/Settings/styles/FlexAlignStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { OrderBumpInterface } from 'common/types/entities/order-bump-interface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'

function OrderBumpSettings({
  entity,
  defaultEntity,
}: {
  entity: OrderBumpInterface
  defaultEntity?: OrderBumpInterface
}) {
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<OrderBumpInterface>()

  const toggleArrowVisibility = () => {
    updateEntity({ ...entity, isArrowHidden: !entity.isArrowHidden })
  }

  return (
    <>
      <Input
        label="entity_settings.order_bump.title"
        value={entity.title}
        update={updateProp('title')}
      />
      <FlexAlignStyle
        align={entity.justifyContent}
        mobileAlign={entity.mobileJustifyContent || entity.justifyContent}
        update={updateProp('justifyContent')}
        mobileUpdate={updateProp('mobileJustifyContent')}
      />
      <NewCheckbox
        checked={!entity.isArrowHidden}
        update={toggleArrowVisibility}
        labelText="entity_settings.order_bump.show_arrow"
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={['color', 'backgroundColor', 'headerBackgroundColor']}
              mobileKeys={[
                'mobileColor',
                'mobileBackgroundColor',
                'mobileHeaderBackgroundColor',
              ]}
            />
          )
        }
      >
        color
      </GroupTitle>
      <ColorPickerControl
        label="settings_styles.header_background_color.label"
        color={entity.headerBackgroundColor}
        mobileColor={
          entity.mobileHeaderBackgroundColor || entity.headerBackgroundColor
        }
        update={updateProp('headerBackgroundColor')}
        mobileUpdate={updateProp('mobileHeaderBackgroundColor')}
        isClearable
      />
      <ColorPickerControl
        label="settings_styles.background_color.label"
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor || entity.backgroundColor}
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        isClearable
      />
      <ColorPickerControl
        update={updateProp('color')}
        color={entity.color}
        mobileColor={entity.mobileColor}
        mobileUpdate={updateProp('mobileColor')}
        label="global_settings.text_color.label"
        isClearable
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'fontSize',
                'fontFileId',
                'fontFamily',
                'fontWeight',
                'fontStyle',
              ]}
              mobileKeys={[
                'mobileFontSize',
                'mobileFontFileId',
                'mobileFontFamily',
                'mobileFontWeight',
                'mobileFontStyle',
              ]}
            />
          )
        }
      >
        typography
      </GroupTitle>
      <FontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize || entity.fontSize}
      />
      <TextFontUpdater entity={entity} update={updateEntity} />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow || entity.boxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <Border
        update={updateProp(isMobile ? 'mobileBorder' : 'border')}
        border={isMobile ? entity.mobileBorder || entity.border : entity.border}
        defaultBorder={defaultEntity?.border}
        mobileDefaultBorder={defaultEntity?.mobileBorder}
      />
      <SizeAndPositionGroup
        padding={
          isMobile ? entity.mobilePadding || entity.padding : entity.padding
        }
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default OrderBumpSettings
